'use client';

import { create } from 'zustand';
import { Entity } from '@/types/entities';
import { Relationship, RelationshipType } from '@/types/relationships';

interface GraphState {
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  expandedNodes: Set<string>;
  filterByEntityType: Set<string>;
  filterByRelationType: Set<RelationshipType>;
  confidenceThreshold: number;
  layoutChoice: 'cose-bilkent' | 'hierarchical' | 'circular';
  commandBarOpen: boolean;
  pinnedNodes: Set<string>;
  setSelectedNode: (id: string | null) => void;
  setSelectedEdge: (id: string | null) => void;
  addExpandedNode: (id: string) => void;
  removeExpandedNode: (id: string) => void;
  toggleEntityTypeFilter: (type: string) => void;
  toggleRelationTypeFilter: (type: RelationshipType) => void;
  setConfidenceThreshold: (threshold: number) => void;
  setLayoutChoice: (layout: 'cose-bilkent' | 'hierarchical' | 'circular') => void;
  setCommandBarOpen: (open: boolean) => void;
  togglePinNode: (id: string) => void;
  clearPins: () => void;
  reset: () => void;
}

export const useGraphStore = create<GraphState>((set) => ({
  selectedNodeId: null,
  selectedEdgeId: null,
  expandedNodes: new Set(),
  filterByEntityType: new Set(),
  filterByRelationType: new Set(),
  confidenceThreshold: 0.5,
  layoutChoice: 'cose-bilkent',
  commandBarOpen: false,
  pinnedNodes: new Set(),

  setSelectedNode: (id) => {
    set({ selectedNodeId: id });
  },

  setSelectedEdge: (id) => {
    set({ selectedEdgeId: id });
  },

  addExpandedNode: (id) => {
    set((state) => {
      const newExpanded = new Set(state.expandedNodes);
      newExpanded.add(id);
      return { expandedNodes: newExpanded };
    });
  },

  removeExpandedNode: (id) => {
    set((state) => {
      const newExpanded = new Set(state.expandedNodes);
      newExpanded.delete(id);
      return { expandedNodes: newExpanded };
    });
  },

  toggleEntityTypeFilter: (type) => {
    set((state) => {
      const newFilter = new Set(state.filterByEntityType);
      if (newFilter.has(type)) {
        newFilter.delete(type);
      } else {
        newFilter.add(type);
      }
      return { filterByEntityType: newFilter };
    });
  },

  toggleRelationTypeFilter: (type) => {
    set((state) => {
      const newFilter = new Set(state.filterByRelationType);
      if (newFilter.has(type)) {
        newFilter.delete(type);
      } else {
        newFilter.add(type);
      }
      return { filterByRelationType: newFilter };
    });
  },

  setConfidenceThreshold: (threshold) => {
    set({ confidenceThreshold: Math.max(0, Math.min(1, threshold)) });
  },

  setLayoutChoice: (layout) => {
    set({ layoutChoice: layout });
  },

  setCommandBarOpen: (open) => {
    set({ commandBarOpen: open });
  },

  togglePinNode: (id) => {
    set((state) => {
      const newPinned = new Set(state.pinnedNodes);
      if (newPinned.has(id)) {
        newPinned.delete(id);
      } else {
        newPinned.add(id);
      }
      return { pinnedNodes: newPinned };
    });
  },

  clearPins: () => {
    set({ pinnedNodes: new Set() });
  },

  reset: () => {
    set({
      selectedNodeId: null,
      selectedEdgeId: null,
      expandedNodes: new Set(),
      filterByEntityType: new Set(),
      filterByRelationType: new Set(),
      confidenceThreshold: 0.5,
      layoutChoice: 'cose-bilkent',
      commandBarOpen: false,
      pinnedNodes: new Set(),
    });
  },
}));
